type Navigate = (to: string) => void;

const normalizePath = (pathname: string) => pathname.replace(/\/$/, "") || "/";
const textOf = (element: Element) => element.textContent?.replace(/\s+/g, " ").trim() ?? "";
const buttonRoutes: ReadonlyArray<readonly [RegExp, string]> = [
  [/ana sayfaya dön/i, "/"],
  [/vaka ile öğren|tüm vakalar/i, "/vaka-ile-ogren"],
  [/klinik kartlar/i, "/klinik-kartlar"],
  [/ilaç güvenliği/i, "/ilac-guvenligi"],
  [/hata atlası/i, "/hata-atlasi"],
  [/nöbet dostu/i, "/nobet-dostu"],
  [/rehberler/i, "/rehberler"],
  [/eğitim modülleri/i, "/egitim-modulleri"],
  [/özel durumlarda resüsitasyon/i, "/ozel-durumlarda-resusitasyon"],
  [/makale radarı/i, "/makale-radar"],
  [/makale kulübü/i, "/makale-kulubu"],
];

const routeForButton = (label: string) => buttonRoutes.find(([pattern]) => pattern.test(label))?.[1];

function setLiveMessage(container: Element, message: string, tone: "info" | "success" | "warning") {
  let output = container.querySelector<HTMLElement>(":scope > [data-source-feedback]");
  if (!output) {
    output = document.createElement("p");
    output.dataset.sourceFeedback = "";
    output.setAttribute("role", "status");
    container.append(output);
  }
  output.dataset.tone = tone;
  output.textContent = message;
}

function updateDecisionProgress(shadow: ShadowRoot) {
  const decisions = [...shadow.querySelectorAll<HTMLElement>("article")].filter((article) => article.querySelector('input[type="radio"]'));
  const completed = decisions.filter((article) => article.dataset.evaluated === "true").length;
  if (!decisions.length) return;
  const progress = shadow.querySelector<HTMLElement>('[style*="width:0%"], [style*="width: 0%"]');
  if (progress) progress.style.width = `${Math.round(completed / decisions.length * 100)}%`;
  const counters = [...shadow.querySelectorAll<HTMLElement>("span, p")].filter((element) => /0\s*(?:<!--.*?-->)?\s*\/\s*\d+|0\/\d+/.test(element.innerHTML));
  counters.forEach((counter) => { counter.textContent = counter.textContent?.replace(/^\s*\d+/, String(completed)) ?? String(completed); });
}

function evaluateDecision(button: HTMLButtonElement, shadow: ShadowRoot) {
  const article = button.closest("article") ?? button.closest("section") ?? button.parentElement;
  const checked = article?.querySelector<HTMLInputElement>('input[type="radio"]:checked');
  const actions = button.parentElement ?? article;
  if (!article || !actions) return;
  if (!checked) {
    setLiveMessage(actions, "Önce bir seçenek belirleyin.", "warning");
    article.querySelector<HTMLInputElement>('input[type="radio"]')?.focus();
    return;
  }
  article.querySelectorAll<HTMLElement>("label").forEach((label) => delete label.dataset.selected);
  const label = checked.closest("label");
  if (label) label.dataset.selected = "true";
  article.dataset.evaluated = "true";
  button.disabled = true;
  const explanation = label?.querySelector("span:last-child")?.textContent?.trim();
  setLiveMessage(actions, explanation || "Seçiminiz kaydedildi. Klinik gerekçeyi gözden geçirin.", "success");
  updateDecisionProgress(shadow);
}

function selectOptionButton(button: HTMLButtonElement) {
  const group = button.closest('[role="radiogroup"]') ?? button.parentElement;
  if (!group) return;
  const options = [...group.querySelectorAll<HTMLButtonElement>(":scope > button, :scope > * > button")]
    .filter((candidate) => /^[A-D](?:\s|\.|$)/i.test(textOf(candidate)));
  options.forEach((option) => {
    const selected = option === button;
    option.setAttribute("aria-pressed", String(selected));
    option.dataset.state = selected ? "on" : "off";
    option.dataset.selected = String(selected);
  });
}

function toggleInferredDisclosure(button: HTMLButtonElement) {
  const panel = button.nextElementSibling instanceof HTMLElement
    ? button.nextElementSibling
    : button.parentElement?.nextElementSibling instanceof HTMLElement ? button.parentElement.nextElementSibling : undefined;
  if (!panel) return false;
  const wasExpanded = button.getAttribute("aria-expanded") !== "false" && !panel.hidden;
  panel.hidden = wasExpanded;
  button.setAttribute("aria-expanded", String(!wasExpanded));
  button.dataset.state = wasExpanded ? "closed" : "open";
  const replaceIn = [...button.querySelectorAll<HTMLElement>("span")].reverse().find((element) => /aç|daralt/i.test(textOf(element))) ?? button;
  replaceIn.textContent = replaceIn.textContent?.replace(/daralt/gi, "aç").replace(/aç(?=$|\s)/gi, wasExpanded ? "aç" : "daralt") ?? "";
  return true;
}

async function copyNearestText(button: HTMLButtonElement) {
  const container = button.closest("article, section, [class*=rounded]") ?? button.parentElement;
  const text = container ? textOf(container).replace(textOf(button), "").trim() : "";
  if (!text) return;
  await navigator.clipboard.writeText(text);
  const previous = textOf(button);
  button.textContent = "Kopyalandı";
  window.setTimeout(() => { button.textContent = previous; }, 1600);
}

export function attachSourceCloneInteractions(shadow: ShadowRoot, navigate: Navigate, knownPaths: ReadonlySet<string>) {
  const onChange = (event: Event) => {
    const input = event.composedPath().find((node): node is HTMLInputElement => node instanceof HTMLInputElement && node.type === "radio");
    if (!input) return;
    const fieldset = input.closest("fieldset");
    fieldset?.querySelectorAll<HTMLElement>("label").forEach((label) => { label.dataset.selected = String(label === input.closest("label")); });
  };

  const onClick = (event: Event) => {
    const button = event.composedPath().find((node): node is HTMLButtonElement => node instanceof HTMLButtonElement);
    if (button) {
      const label = textOf(button).toLocaleLowerCase("tr-TR");
      const buttonRoute = routeForButton(label);
      if (buttonRoute && knownPaths.has(buttonRoute)) navigate(buttonRoute);
      const isOption = /^[a-d](?:\s|\.|$)/i.test(label);
      if (isOption) selectOptionButton(button);
      if (label === "önceki sayfa") window.history.back();
      if (label.includes("kararı değerlendir")) evaluateDecision(button, shadow);
      if (label.includes("kopyala")) void copyNearestText(button);
      if (label.includes("yazdır")) window.print();
      if (!button.hasAttribute("aria-controls") && (/(?:aç|daralt)(?:\s|$)/i.test(label) || label.endsWith("?"))) toggleInferredDisclosure(button);
      const controls = button.getAttribute("aria-controls");
      if (controls) {
        const panel = shadow.getElementById(controls);
        const expanded = button.getAttribute("aria-expanded") === "true";
        button.setAttribute("aria-expanded", String(!expanded));
        button.dataset.state = expanded ? "closed" : "open";
        if (panel) panel.hidden = expanded;
      }
      if (button.getAttribute("role") === "tab") {
        const tabList = button.closest('[role="tablist"]');
        tabList?.querySelectorAll<HTMLButtonElement>('[role="tab"]').forEach((tab) => {
          const selected = tab === button;
          tab.setAttribute("aria-selected", String(selected));
          tab.dataset.state = selected ? "active" : "inactive";
          const panelId = tab.getAttribute("aria-controls");
          if (panelId) { const panel = shadow.getElementById(panelId); if (panel) panel.hidden = !selected; }
        });
      }
      if (!isOption && button.hasAttribute("aria-pressed")) {
        const pressed = button.getAttribute("aria-pressed") === "true";
        button.setAttribute("aria-pressed", String(!pressed));
        button.dataset.state = pressed ? "off" : "on";
      }
    }
    const target = event.composedPath().find((node): node is HTMLAnchorElement => node instanceof HTMLAnchorElement);
    if (!target) return;
    const url = new URL(target.href, window.location.href);
    if (url.origin !== window.location.origin && url.origin !== "https://pedemup.org") return;
    if (!knownPaths.has(normalizePath(url.pathname))) return;
    event.preventDefault();
    navigate(`${url.pathname}${url.search}${url.hash}`);
  };

  shadow.addEventListener("change", onChange);
  shadow.addEventListener("click", onClick);
  return () => {
    shadow.removeEventListener("change", onChange);
    shadow.removeEventListener("click", onClick);
  };
}

export const sourceInteractionStyles = `
  label[data-selected="true"] { border-color: #0f8f83 !important; background: #ecfdf8 !important; box-shadow: 0 0 0 2px rgba(15,143,131,.12); }
  button[data-selected="true"] { border-color: #0f8f83 !important; box-shadow: 0 0 0 2px rgba(15,143,131,.14); }
  [data-source-feedback] { margin-top: .75rem; padding: .75rem 1rem; border-radius: .75rem; font-size: .8125rem; line-height: 1.5; }
  [data-source-feedback][data-tone="success"] { color: #075f58; background: #e7f8f4; border: 1px solid #8ee4d6; }
  [data-source-feedback][data-tone="warning"] { color: #7a4b00; background: #fff8df; border: 1px solid #f5d36a; }
`;
